import * as React from 'react'
import { cn } from '../../lib/utils'
import { formFieldBoxClass } from './form-field-box'
import { Label } from './label'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
	label?: string
	error?: string
	hint?: string
	/** Inline-start adornment (e.g. `серия`, `№`, `от`). */
	prefix?: React.ReactNode
	/** Inline-end adornment (e.g. `%`, `₽`, `м²`, `Дня`). */
	suffix?: React.ReactNode
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, label, error, hint, prefix, suffix, disabled, id, ...props }, ref) => {
		const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')

		const box = (
			<div
				className={cn(
					formFieldBoxClass,
					error && 'border-(--color-destructive) shadow-[0_0_0_2px_var(--color-background),0_0_0_4px_var(--color-destructive-hover)] focus-within:ring-0',
					disabled && 'cursor-not-allowed opacity-60 bg-(--color-muted)',
					className
				)}
			>
				{prefix !== undefined && (
					<span className="text-(--color-muted-foreground)">{prefix}</span>
				)}
				<input
					id={inputId}
					ref={ref}
					disabled={disabled}
					className="flex-1 min-w-0 bg-transparent placeholder:text-(--color-muted-foreground) focus:outline-none disabled:cursor-not-allowed disabled:bg-transparent"
					{...props}
				/>
				{suffix !== undefined && (
					<span className="text-(--color-muted-foreground)">{suffix}</span>
				)}
			</div>
		)

		if (!label && !error && !hint) return box

		return (
			<div className="flex w-full flex-col gap-2">
				{label && <Label htmlFor={inputId}>{label}</Label>}
				{box}
				{error && <p className="text-sm leading-5 text-(--color-destructive)">{error}</p>}
				{hint && !error && <p className="text-sm leading-5 text-(--color-muted-foreground)">{hint}</p>}
			</div>
		)
	}
)

Input.displayName = 'Input'

export { Input }
